import { ReactNode, useState } from 'react'
import { Add, Check, InfoOutlined, Remove } from '@mui/icons-material'
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  Slider,
  Tooltip,
  Typography,
} from '@mui/material'
import { MaskedInput } from '@/components/masked-input/masked-input'

/** Ícone "i" ao lado do rótulo: abre a explicação no toque e no hover. */
export function InfoTip({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Tooltip
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        title={<Box sx={{ display: 'grid', gap: 1, p: 0.5, fontSize: 13 }}>{children}</Box>}
        arrow
      >
        <IconButton
          size="small"
          aria-label={`Saiba mais: ${label}`}
          onClick={() => setOpen((current) => !current)}
          sx={{ p: 0.25, color: 'text.secondary' }}
        >
          <InfoOutlined sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </ClickAwayListener>
  )
}

export function Field({
  label,
  help,
  info,
  children,
}: {
  label?: string
  help?: string
  /** Explicação do rótulo, no ícone "i". */
  info?: ReactNode
  children: ReactNode
}) {
  return (
    <Box sx={{ display: 'grid', gap: 0.75 }}>
      {label && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {label}
          </Typography>
          {info && <InfoTip label={label}>{info}</InfoTip>}
        </Box>
      )}
      {children}
      {help && (
        <Typography variant="body2" color="text.secondary">
          {help}
        </Typography>
      )}
    </Box>
  )
}

export function ComboSwitch({
  value,
  onChange,
  yesLabel,
  noLabel,
  ariaLabel,
}: {
  value: boolean
  onChange: (value: boolean) => void
  yesLabel: string
  noLabel: string
  ariaLabel: string
}) {
  const options = [
    { on: true, label: yesLabel },
    { on: false, label: noLabel },
  ]
  return (
    <Box
      role="radiogroup"
      aria-label={ariaLabel}
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 0.5,
        p: 0.75,
        borderRadius: 100,
        border: 1,
        borderColor: 'creditJourney.switchBorder',
        bgcolor: 'creditJourney.switchTrack',
        width: '100%',
        maxWidth: 400,
        mx: 'auto',
        mt: 2.25,
      }}
    >
      {options.map((option) => {
        const selected = value === option.on
        return (
          <Box
            key={option.label}
            component="button"
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.on)}
            sx={{
              font: 'inherit',
              fontSize: 14,
              fontWeight: 500,
              border: 0,
              borderRadius: 100,
              py: 1.5,
              px: 1,
              cursor: 'pointer',
              transition: 'background .15s, color .15s',
              bgcolor: selected
                ? option.on
                  ? 'primary.light'
                  : 'creditJourney.switchOff'
                : 'transparent',
              color: selected
                ? option.on
                  ? 'common.white'
                  : 'creditJourney.switchOffText'
                : 'text.secondary',
              boxShadow: selected ? 2 : 'none',
            }}
          >
            {option.label}
          </Box>
        )
      })}
    </Box>
  )
}

function toggleSelection(
  value: string | string[],
  next: string,
  multiple: boolean,
): string | string[] {
  if (!multiple) return next
  const current = Array.isArray(value) ? value : []
  return current.includes(next)
    ? current.filter((item) => item !== next)
    : [...current, next]
}

export function OptionCards({
  value,
  options,
  onChange,
  multiple = false,
}: {
  value: string | string[]
  options: { value: string; label: string; description?: string }[]
  onChange: (value: string | string[]) => void
  multiple?: boolean
}) {
  const selected = Array.isArray(value) ? value : [value]

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1,
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
      }}
    >
      {options.map((option) => {
        const on = selected.includes(option.value)
        return (
          <Button
            key={option.value}
            onClick={() => onChange(toggleSelection(value, option.value, multiple))}
            sx={{
              justifyContent: 'space-between',
              textAlign: 'left',
              minHeight: 54,
              px: 2,
              py: 1.5,
              borderRadius: 1.5,
              border: on ? 2 : 1,
              borderColor: on ? 'primary.light' : 'divider',
              bgcolor: on ? 'primary.50' : 'background.paper',
              color: 'text.primary',
              textTransform: 'none',
              fontSize: 16,
              fontWeight: 400,
            }}
          >
            <Box>
              <Box>{option.label}</Box>
              {option.description && (
                <Typography variant="caption" color="text.secondary">
                  {option.description}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: multiple ? '6px' : '50%',
                border: 2,
                borderColor: on ? 'primary.main' : 'divider',
                bgcolor: multiple && on ? 'primary.main' : 'transparent',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              {on && !multiple && (
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'primary.main' }} />
              )}
              {on && multiple && <Check sx={{ fontSize: 16, color: 'primary.contrastText' }} />}
            </Box>
          </Button>
        )
      })}
    </Box>
  )
}

export function PillGroup({
  label,
  value,
  options,
  onChange,
  help,
  info,
  multiple = false,
}: {
  label?: string
  value: string | string[]
  options: { value: string; label: string }[]
  onChange: (value: string | string[]) => void
  help?: string
  info?: ReactNode
  multiple?: boolean
}) {
  const selected = Array.isArray(value) ? value : [value]

  return (
    <Field label={label} help={help} info={info}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {options.map((option) => {
          const on = selected.includes(option.value)
          return (
            <Button
              key={option.value}
              onClick={() => onChange(toggleSelection(value, option.value, multiple))}
              sx={{
                borderRadius: 100,
                minHeight: 44,
                px: 2,
                textTransform: 'none',
                border: 1,
                borderColor: on ? 'primary.light' : 'divider',
                bgcolor: on ? 'primary.light' : 'background.paper',
                color: on ? 'primary.contrastText' : 'text.primary',
              }}
            >
              {option.label}
            </Button>
          )
        })}
      </Box>
    </Field>
  )
}

export function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <Button
      onClick={() => onChange(!checked)}
      sx={{
        justifyContent: 'space-between',
        textTransform: 'none',
        color: 'text.primary',
        border: 1,
        borderColor: checked ? 'primary.light' : 'divider',
        bgcolor: checked ? 'primary.50' : 'background.paper',
        borderRadius: 1.5,
        px: 2,
        minHeight: 52,
      }}
    >
      {label}
      <Box
        sx={{
          width: 42,
          height: 24,
          borderRadius: 100,
          bgcolor: checked ? 'primary.light' : 'action.hover',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            bgcolor: 'background.paper',
            position: 'absolute',
            top: 3,
            left: checked ? 21 : 3,
          }}
        />
      </Box>
    </Button>
  )
}

export function MoneyField({
  label,
  value,
  onChange,
  help,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  help?: string
}) {
  return (
    <Field label={label} help={help}>
      <MaskedInput
        format="currency"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        fullWidth
      />
    </Field>
  )
}

function formatReaisInt(amount: number) {
  return amount.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  })
}

export function AmountStepper({
  value,
  min,
  max,
  step,
  onChange,
  format = formatReaisInt,
  label = 'Valor por mês',
  stepLabel,
  showEnds = false,
}: {
  value: number
  min: number
  max: number
  step: number
  onChange: (value: number) => void
  format?: (value: number) => string
  label?: string
  /** Nome do passo nos botões. Sem valor, usa o valor formatado. */
  stepLabel?: string
  showEnds?: boolean
}) {
  const unit = stepLabel ?? format(step)
  return (
    <Box sx={{ display: 'grid', gap: 2, maxWidth: 460, mx: 'auto', width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <IconButton
          aria-label={`Diminuir ${unit}`}
          onClick={() => onChange(Math.max(min, value - step))}
          disabled={value <= min}
          sx={{ border: 1, borderColor: 'divider' }}
        >
          <Remove />
        </IconButton>
        <Typography sx={{ fontSize: 40, fontWeight: 500 }}>{format(value)}</Typography>
        <IconButton
          aria-label={`Aumentar ${unit}`}
          onClick={() => onChange(Math.min(max, value + step))}
          disabled={value >= max}
          sx={{ border: 1, borderColor: 'divider' }}
        >
          <Add />
        </IconButton>
      </Box>
      <Slider
        min={min}
        max={max}
        step={step}
        value={Math.min(Math.max(value, min), max)}
        onChange={(_, next) => onChange(next as number)}
        aria-label={label}
      />
      {showEnds && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: -1.5,
            fontSize: 12.5,
            color: 'text.secondary',
          }}
        >
          <span>{format(min)}</span>
          <span>{format(max)}</span>
        </Box>
      )}
    </Box>
  )
}
